import { useEffect, useState } from 'react'
import { Button, Select, Tooltip } from '@truepill/react-capsule'
import { isOtcOnlyOrder } from '@truepill/tpos-data-util'
import { Link, useLocation } from '@truepill/tpos-react-router'
import { BULK_ACTION_SOURCE_QUEUES, isBulkActionAllowedFromQueue, OrderType } from '@truepill/tpos-types'
import type {
  BulkActionType,
  OrderStatus,
  BulkActionParams,
  BulkActionSourceQueue,
  RerouteParams,
  TriageParams,
  ResolveTriageParams,
} from '@truepill/tpos-types'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useCreateBulkAction } from 'hooks/useBulkActions'
import { FULFILLMENT_QUEUES } from 'hooks/useFulfillmentQueue'
import type { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import useSelectedLocations from 'hooks/useSelectedLocations'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useSelectionContext } from 'providers/SelectionProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { usePlusClient } from 'providers/VisionRouter'
import { X } from 'react-feather'
import { goToFulfillmentOrder } from 'routes'
import styled from 'styled-components'
import { capsuleGrayColor, capsuleLightGreyColor, borderColor, primaryColor } from 'styles/styleVariables'
import { getPatientFromOrder } from 'utils'
import {
  buildActionStep,
  formatStepLabel,
  getBulkActionParamsSummary,
  getBulkActionLabel,
  getBulkActionOptions,
  getBulkActionTypeFromLabel,
} from 'utils/bulkActions'
import { getQueueNameFromPath } from 'utils/fulfillmentQueue'
import BulkActionConfirmationModal from '../BulkActionConfirmationModal'
import BulkRerouteModal from '../configModals/BulkRerouteModal'
import BulkResolveTriageModal from '../configModals/BulkResolveTriageModal'
import BulkTriageModal from '../configModals/BulkTriageModal'

const MAX_STEPS = 2

type ConfiguredStep = { action: BulkActionType; params?: BulkActionParams }

const getQueueOrderStatus = (queueName?: string): OrderStatus | undefined => {
  if (!queueName) return undefined
  const queue = FULFILLMENT_QUEUES[queueName as FulfillmentQueueName]
  return queue?.orderStages?.[0]
}

type Props = { onCreateSuccess: () => void }

const NewTab = ({ onCreateSuccess }: Props): JSX.Element => {
  const { pathname, search } = useLocation()
  const queueName = getQueueNameFromPath(pathname)
  const sourceQueue = BULK_ACTION_SOURCE_QUEUES.find(q => q === queueName)

  const { tokenContext } = usePlusClient()
  const { showModal } = useModalContext()
  const { getLocationNameById } = useTPCacheContext()
  const { uniqueOrders, isSingleLocation, currentLocationId } = useSelectedLocations()
  const { initSelections } = useSelectionContext()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { createBulkAction, loading } = useCreateBulkAction()

  const [configuredSteps, setConfiguredSteps] = useState<ConfiguredStep[]>([])

  const hasMultipleOrders = uniqueOrders.length > 1
  const hasConfiguredSteps = configuredSteps.length > 0
  const allStepsConfigured = hasConfiguredSteps && configuredSteps.every(cs => !!cs.params)
  const canCreate = hasMultipleOrders && isSingleLocation && allStepsConfigured

  // OTC-only orders unlock reroute from the Packing queue (after fill), where it is otherwise blocked.
  const allOtcOnly = uniqueOrders.length > 0 && uniqueOrders.every(isOtcOnlyOrder)
  // Orders with OTC products (OTC-only and Rx+OTC) unlock extra Packing triage reasons.
  const ordersWithOtcProducts = uniqueOrders.length > 0 && uniqueOrders.every(order => !!order.otcProducts?.length)

  useEffect(() => {
    if (uniqueOrders.length === 0) {
      setConfiguredSteps([])
      return
    }
    setConfiguredSteps(prev =>
      prev.filter(cs => !sourceQueue || isBulkActionAllowedFromQueue(cs.action, sourceQueue, allOtcOnly)),
    )
  }, [sourceQueue, uniqueOrders.length, allOtcOnly])

  const baseOptions = getBulkActionOptions(sourceQueue, allOtcOnly)
  const remainingOptions = baseOptions.filter(label => {
    const type = getBulkActionTypeFromLabel(label)
    return type ? !configuredSteps.some(cs => cs.action === type) : false
  })
  const showSelect = configuredSteps.length < MAX_STEPS && remainingOptions.length > 0

  const tooltipText = !hasMultipleOrders
    ? 'Select at least two orders to get started'
    : !isSingleLocation
    ? 'Bulk Actions can only be performed from a single location queue'
    : !hasConfiguredSteps
    ? 'Add an action to get started'
    : !allStepsConfigured
    ? 'Configure every action before starting'
    : ''

  const updateRow = (rowIdx: number, updater: (current: ConfiguredStep) => ConfiguredStep) =>
    setConfiguredSteps(prev => prev.map((cs, idx) => (idx === rowIdx ? updater(cs) : cs)))

  const removeRow = (rowIdx: number) => setConfiguredSteps(prev => prev.filter((_, idx) => idx !== rowIdx))

  const addAction = (label: string) => {
    const action = getBulkActionTypeFromLabel(label)
    if (!action) return
    if (configuredSteps.some(cs => cs.action === action)) return
    if (configuredSteps.length >= MAX_STEPS) return
    setConfiguredSteps(prev => [...prev, { action }])
  }

  const openConfigModal = (rowIdx: number) => {
    const row = configuredSteps[rowIdx]
    if (!row) return

    const handleConfirm = (params: BulkActionParams) => updateRow(rowIdx, current => ({ ...current, params }))

    switch (row.action) {
      case 'reroute': {
        const rerouteInitial =
          (row.params as RerouteParams) ?? (currentLocationId ? { locationId: currentLocationId } : undefined)
        showModal(() => (
          <BulkRerouteModal
            onConfirm={handleConfirm}
            initialParams={rerouteInitial}
            sourceLocationId={currentLocationId}
          />
        ))
        break
      }
      case 'triage':
        showModal(() => (
          <BulkTriageModal
            onConfirm={handleConfirm}
            initialParams={row.params as TriageParams | undefined}
            orderStatus={getQueueOrderStatus(queueName)}
            hasOtc={ordersWithOtcProducts}
          />
        ))
        break
      case 'resolve_triage':
        showModal(() => (
          <BulkResolveTriageModal
            onConfirm={handleConfirm}
            initialParams={row.params as ResolveTriageParams | undefined}
          />
        ))
        break
    }
  }

  const handleStartBulkAction = async () => {
    if (!allStepsConfigured || !currentLocationId || !sourceQueue) return
    try {
      const steps = configuredSteps.map(cs => buildActionStep(cs.action, cs.params!))
      const created = await createBulkAction({
        orderIds: uniqueOrders.map(o => o._id),
        steps,
        locationId: currentLocationId,
        sourceQueue: sourceQueue as BulkActionSourceQueue,
      })
      if (!created) throw new Error('Failed to start bulk action')

      initSelections([])
      setConfiguredSteps([])
      if (tokenContext?.locationId && String(tokenContext.locationId) === String(created.locationId)) {
        onCreateSuccess()
        showSuccessToast('Bulk action started')
      } else {
        showSuccessToast('Bulk action started. History is scoped to your assigned location.')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start bulk action'
      showErrorToast(message)
    }
  }

  const openConfirmationModal = () => {
    if (!allStepsConfigured || !currentLocationId) return
    const steps = configuredSteps.map(cs => buildActionStep(cs.action, cs.params!))
    const locationName = getLocationNameById(currentLocationId) || currentLocationId
    showModal(() => (
      <BulkActionConfirmationModal
        steps={steps}
        orderCount={uniqueOrders.length}
        locationName={locationName}
        sourceQueue={sourceQueue}
        onConfirm={handleStartBulkAction}
      />
    ))
  }

  return (
    <>
      <SectionTitle>Selected Orders ({uniqueOrders.length})</SectionTitle>
      {uniqueOrders.length ? (
        <OrderListContainer>
          {uniqueOrders.map(order => {
            const isMultiPetOrder = order.orderType === OrderType.MultiPet
            const patient = !isMultiPetOrder ? getPatientFromOrder(order) : undefined
            const firstName = order.guestPatient ? order.guestPatient.firstName : patient?.firstName
            const lastName = order.guestPatient ? order.guestPatient.lastName : patient?.lastName

            return (
              <OrderRow
                key={order._id}
                to={goToFulfillmentOrder({
                  fulfillmentQueueName: queueName as FulfillmentQueueName,
                  orderId: order._id,
                  search,
                })}
              >
                <OrderNumber>{order.coreOrderToken}</OrderNumber>
                <PatientName>{`${firstName ?? ''} ${lastName ?? ''}`.trim()}</PatientName>
              </OrderRow>
            )
          })}
        </OrderListContainer>
      ) : (
        <EmptyState>Select orders from a queue to get started</EmptyState>
      )}

      {showSelect && (
        <Select
          variant="small"
          label="Action"
          options={remainingOptions}
          value=""
          onChange={value => {
            if (!value) return
            addAction(value)
          }}
          placeholder={configuredSteps.length === 0 ? 'Choose an action...' : 'Add another action...'}
        />
      )}

      {configuredSteps.map((step, idx) => (
        <ConfiguredStepRow
          key={`${step.action}-${idx}`}
          stepIndex={idx}
          action={step.action}
          paramsSummary={
            step.params
              ? getBulkActionParamsSummary(buildActionStep(step.action, step.params), getLocationNameById)
              : undefined
          }
          onConfigure={() => openConfigModal(idx)}
          onRemove={() => removeRow(idx)}
        />
      ))}

      <Tooltip
        variant="brand-dark"
        label={tooltipText}
        css={{
          ...(!tooltipText ? { display: 'none' } : {}),
          zIndex: 1000,
        }}
      >
        <StartButton variant="primary" size="sm" disabled={!canCreate || loading} onClick={openConfirmationModal}>
          {loading ? 'Starting Bulk Action...' : 'Start Bulk Action'}
        </StartButton>
      </Tooltip>
    </>
  )
}

type ConfiguredStepRowProps = {
  stepIndex: number
  action: BulkActionType
  paramsSummary: string | undefined
  onConfigure: () => void
  onRemove: () => void
}

const ConfiguredStepRow = ({
  stepIndex,
  action,
  paramsSummary,
  onConfigure,
  onRemove,
}: ConfiguredStepRowProps): JSX.Element => (
  <ConfigureSection>
    <ConfigureRow>
      <ConfigureLabel>{formatStepLabel(stepIndex, action)}</ConfigureLabel>
      {paramsSummary ? (
        <ParamsSummary>{paramsSummary}</ParamsSummary>
      ) : (
        <ParamsPlaceholder>Not configured</ParamsPlaceholder>
      )}
      <Button size="xs" variant="primary-outline" onClick={onConfigure}>
        Configure
      </Button>
      <RemoveButton type="button" aria-label={`Remove ${getBulkActionLabel(action)}`} onClick={onRemove}>
        <X size={14} />
      </RemoveButton>
    </ConfigureRow>
  </ConfigureSection>
)

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
`

const OrderListContainer = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  border: 1px solid ${borderColor};
  border-radius: 4px;
  margin: 12px 0;
`

const OrderRow = styled(Link)`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 1px solid ${borderColor};
  text-decoration: none;
  color: black;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${capsuleGrayColor};
    color: ${primaryColor};
  }

  &:visited {
    color: black;
  }

  &:visited:hover {
    color: ${primaryColor};
  }
`

const OrderNumber = styled.span`
  font-size: 12px;
  font-weight: 600;
  width: 40%;
  flex-shrink: 0;
`

const PatientName = styled.span`
  font-size: 12px;
  width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyState = styled.div`
  color: ${capsuleLightGreyColor};
  font-size: 13px;
  padding: 16px 0;
  text-align: center;
`

const ConfigureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 10px 0;
`

const ConfigureRow = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
`

const ConfigureLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
`

const ParamsSummary = styled.span`
  font-size: 12px;
  color: ${capsuleLightGreyColor};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`

const ParamsPlaceholder = styled(ParamsSummary)`
  font-style: italic;
`

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${capsuleLightGreyColor};
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;

  &:hover {
    color: ${primaryColor};
    background: ${capsuleGrayColor};
  }
`

const StartButton = styled(Button)`
  width: 100%;
  margin-top: 12px;
`

export default NewTab
